// Development page — player drawer: per block, all 7 metrics as bars of ratio vs the reference
// group (dashed line = group median, shaded = ±10%), the reference group by name, the numbers
// behind each bar and the full sentences. Full screen below ~900px. Clicking a bar row or a group
// median opens a small second popup with the reference players' own values for that metric — where
// the group median comes from.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  METRICS, BLOCKS, BLOCK_LABEL, LONG_LABEL, NOT_PICKABLE_NOTE, RatioBar, RatioAxis, ratioDomain,
  referenceText, fmtPct, fmtValue, fmtDate, fmtOfTarget, relText,
} from './developmentShared';

const cellTh = { textAlign: 'right', padding: '4px 6px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
// One bar row: metric | bar | % | tag. Fixed column widths so every row (each its own button)
// lines up with the axis row.
const BAR_ROW = { display: 'grid', gridTemplateColumns: '46px 1fr 52px 112px', columnGap: 10, alignItems: 'center', width: '100%' };
const cellTd = { textAlign: 'right', padding: '4px 6px', fontSize: 12, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };

// What a metric row is, in words: the picked key point, or why it can't be one.
function rowTag(blockKey, block, metricKey) {
  const kp = block.keyPoints;
  if (kp.specialisation && kp.specialisation.metric === metricKey) return { text: 'Specialisation', strong: true };
  if (kp.improvement && kp.improvement.metric === metricKey) return { text: 'Area to improve', strong: true };
  if (block.metrics[metricKey].noTarget) return { text: 'no target', title: 'No match target for this metric at this position — shown, never a key point' };
  if ((block.notPickable || []).includes(metricKey)) return { text: 'not pickable', title: `${BLOCK_LABEL[blockKey]} ${metricKey.toUpperCase()}: ${NOT_PICKABLE_NOTE}` };
  if (block.metrics[metricKey].tooFewPoints && block.state === 'ok') return { text: 'too few points', title: `Only ${block.metrics[metricKey].n} valid ${blockKey === 'match' ? 'matches' : 'scenarios'} for this metric` };
  return null;
}

function BlockSection({ blockKey, block, domain, onDetail }) {
  const unit = blockKey === 'match' ? 'matches' : 'scenarios';
  const usable = block.state === 'ok' && !block.keyPoints.state;
  const ref = referenceText(block);
  const vsTarget = block.referenceUsed === 'target';
  return (
    <section style={{ marginTop: 14, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{BLOCK_LABEL[blockKey]}</h4>
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          {vsTarget ? 'mean % of target per match, as on Match Performance — competitive, 60+ min'
            : blockKey === 'match' ? 'mean per 90 over competitive matches of 60+ min' : 'mean of scenario total ÷ squad median, training sessions only'}
          {' · '}n = {block.nValid} {unit}
        </span>
      </div>
      {ref && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          {ref}
          {block.referenceUsed !== 'position' && !vsTarget && <span style={{ color: 'var(--text-muted)' }}> — {blockKey === 'match' ? 'no match target for this position' : 'position group too small'}</span>}
        </div>
      )}
      {!usable ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>{block.keyPoints.state?.text}</div>
      ) : (
        <div style={{ marginTop: 10, display: 'grid', rowGap: 2 }}>
          <div style={BAR_ROW}><span /><RatioAxis domain={domain} mode={vsTarget ? 'target' : 'group'} /><span /><span /></div>
          {METRICS.map(m => {
            const mm = block.metrics[m.key];
            const tag = rowTag(blockKey, block, m.key);
            const title = vsTarget
              ? `${LONG_LABEL[m.key]}: ${mm.noTarget ? 'no target' : `${fmtOfTarget(mm.pctOfTarget)} of target (${fmtValue('match', m.key, mm.target)})`}, avg ${fmtValue('match', m.key, mm.value)} per 90, n = ${mm.n} — click for match by match`
              : `${LONG_LABEL[m.key]}: ${fmtValue(blockKey, m.key, mm.value)} vs group median ${fmtValue(blockKey, m.key, mm.groupMedian)} (${fmtPct(mm.pctVsGroup)}, n = ${mm.n}) — click for the group's values`;
            return (
              <button key={m.key} type="button" className="dev-barrow" style={BAR_ROW} title={title}
                onClick={() => onDetail(blockKey, m.key)} aria-label={`${LONG_LABEL[m.key]} ${BLOCK_LABEL[blockKey]}: ${vsTarget ? 'show match by match' : "show the group's values"}`}>
                <span style={{ fontSize: 12, fontWeight: tag?.strong ? 700 : 500, color: 'var(--text-primary)' }}>{m.label}</span>
                <RatioBar ratio={mm.ratio} domain={domain} />
                <span style={{ fontSize: 12, fontWeight: 600, textAlign: 'right', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{relText(mm)}</span>
                <span title={tag?.title} style={{ fontSize: 11, color: tag?.strong ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: tag?.strong ? 700 : 500, whiteSpace: 'nowrap' }}>
                  {tag?.text || ''}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ overflowX: 'auto', marginTop: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...cellTh, textAlign: 'left' }}>Metric</th>
              <th style={cellTh}>{blockKey === 'match' ? (vsTarget ? 'Avg /90' : 'Value /90') : 'Value'}</th>
              <th style={cellTh}>{vsTarget ? 'Target' : 'Group median'}</th>
              <th style={cellTh}>{vsTarget ? '% of target' : 'vs group'}</th>
              <th style={cellTh}>n</th>
            </tr>
          </thead>
          <tbody>
            {METRICS.map(m => {
              const mm = block.metrics[m.key];
              return (
                <tr key={m.key}>
                  <td style={{ ...cellTd, textAlign: 'left' }} title={LONG_LABEL[m.key]}>{m.label}</td>
                  <td style={cellTd}>{fmtValue(blockKey, m.key, mm.value)}</td>
                  <td style={cellTd}>
                    {vsTarget
                      ? (mm.noTarget ? <span style={{ color: 'var(--text-muted)' }}>no target</span> : fmtValue('match', m.key, mm.target))
                      : mm.groupMedian != null && usable
                        ? <button type="button" className="dev-linkbtn" onClick={() => onDetail(blockKey, m.key)} title="Show the group's values">{fmtValue(blockKey, m.key, mm.groupMedian)}</button>
                        : fmtValue(blockKey, m.key, mm.groupMedian)}
                  </td>
                  <td style={cellTd}>
                    {vsTarget && mm.ratio != null && usable
                      ? <button type="button" className="dev-linkbtn" onClick={() => onDetail(blockKey, m.key)} title="Show match by match">{relText(mm)}</button>
                      : relText(mm)}
                  </td>
                  <td style={{ ...cellTd, color: mm.tooFewPoints ? 'var(--text-muted)' : cellTd.color }}>{mm.n ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {vsTarget && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
            Target = the {block.referenceName} match-day target, the same one Match Performance uses (pro-rated for matches under 90 min).
            Avg /90 is shown for reference; the key points use the % of target.
          </div>
        )}
        {block.groupSize != null && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
            Group size {block.groupSize} (incl. this player) · group median = median of the other {block.groupSize - 1} players
          </div>
        )}
      </div>
    </section>
  );
}

const SCENARIO_EXCLUDED = { rehab: 'rehab', no_training: 'no training', estimated: 'estimated', zero: '0 — not counted' };

// How this player's training value is built: per scenario his total, the squad median of that
// scenario and the ratio; the value is the mean of the counted ratios. Collapsed by default.
function ScenarioBreakdown({ player, metricKey }) {
  const t = player.training;
  const mm = t.metrics[metricKey];
  const m = x => fmtValue('match', metricKey, x);
  return (
    <details style={{ marginTop: 8 }}>
      <summary style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>
        How {player.name}'s {fmtValue('training', metricKey, mm.value)} is built — scenario by scenario
      </summary>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
        <thead>
          <tr>
            <th style={{ ...cellTh, textAlign: 'left' }}>Scenario</th>
            <th style={cellTh}>His total</th>
            <th style={cellTh}>Squad median</th>
            <th style={cellTh}>Ratio</th>
          </tr>
        </thead>
        <tbody>
          {(t.scenarios || []).map(w => {
            const reason = w.excluded || (w.excludedMetrics && w.excludedMetrics[metricKey]) || null;
            const muted = reason ? { color: 'var(--text-muted)' } : null;
            return (
              <tr key={w.id}>
                <td style={{ ...cellTd, textAlign: 'left', ...muted }} title={`${w.scenario}: ${fmtDate(w.start)} – ${fmtDate(w.end)}`}>
                  {fmtDate(w.start).slice(0, 5)} → {fmtDate(w.end).slice(0, 5)} <span style={{ color: 'var(--text-muted)' }}>{w.match || w.scenario}</span>
                </td>
                <td style={{ ...cellTd, ...muted }}>{w.raw ? m(w.raw[metricKey]) : '—'}</td>
                <td style={{ ...cellTd, ...muted }}>{w.squadMedian ? m(w.squadMedian[metricKey]) : '—'}</td>
                <td style={{ ...cellTd, ...muted }}>{reason ? SCENARIO_EXCLUDED[reason] || reason : fmtValue('training', metricKey, w.ratios && w.ratios[metricKey])}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>
        Mean of the {mm.n} counted ratios = <strong style={{ color: 'var(--text-primary)' }}>{fmtValue('training', metricKey, mm.value)}</strong>.
        A scenario runs from the day after a match up to the next match day. Training sessions only: no match and no Match-RUNS compensation.
        Squad median = all outfield players with training load in that scenario, rehab left out.
      </div>
    </details>
  );
}

// Match vs target, match by match: per valid match his total, the target for that match (pro-rated
// below 90 minutes, exactly as Match Performance) and the % of target; the key point uses the mean.
function MatchTargetDetail({ player, metricKey, onClose }) {
  const closeRef = useRef(null);
  const block = player.match;
  const mm = block.metrics[metricKey];
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const v = x => fmtValue('match', metricKey, x);
  return (
    <div role="presentation" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={`${LONG_LABEL[metricKey]} — match by match`} onClick={e => e.stopPropagation()}
        style={{ width: 'min(500px, 100%)', maxHeight: '100%', overflowY: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 12, padding: 14, boxSizing: 'border-box', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{LONG_LABEL[metricKey]} · Match</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
              vs the {block.referenceName} match target{mm.target ? ` (${v(mm.target)})` : ''} · competitive matches of 60+ min
            </div>
          </div>
          <button ref={closeRef} onClick={onClose} aria-label="Close"
            style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', borderRadius: 8, padding: 5, cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
        {mm.noTarget ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 10 }}>No match target for this metric at this position — shown, never a key point.</div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
              <thead>
                <tr>
                  <th style={{ ...cellTh, textAlign: 'left' }}>Match</th>
                  <th style={cellTh}>Min</th>
                  <th style={cellTh}>Total</th>
                  <th style={cellTh}>Target</th>
                  <th style={cellTh}>% of target</th>
                </tr>
              </thead>
              <tbody>
                {(block.points || []).map(p => {
                  const muted = p.excluded ? { color: 'var(--text-muted)' } : null;
                  const pct = p.pctOfTarget ? p.pctOfTarget[metricKey] : null;
                  return (
                    <tr key={`${p.sessionId}-${p.game}`}>
                      <td style={{ ...cellTd, textAlign: 'left', ...muted }}>{fmtDate(p.date).slice(0, 5)} <span style={{ color: 'var(--text-muted)' }}>{p.opponent || ''}</span></td>
                      <td style={{ ...cellTd, ...muted }}>{Math.round(p.minutes)}</td>
                      <td style={{ ...cellTd, ...muted }}>{p.raw ? v(p.raw[metricKey]) : '—'}</td>
                      <td style={{ ...cellTd, ...muted }}>{v(mm.target * Math.min(p.minutes, 90) / 90)}</td>
                      <td style={{ ...cellTd, ...muted }}>{p.excluded ? p.excluded : fmtOfTarget(pct)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.5 }}>
              Mean of the {mm.n} matches = <strong>{fmtOfTarget(mm.pctOfTarget)}</strong> of target.
              Target pro-rated below 90 minutes and never raised above it — the same rule as Match Performance.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Where a group median comes from: every player in the reference group with his own value for
// this metric, sorted high → low, the middle value(s) marked, and this player for comparison (not
// in the median). The median itself is the API's number; only the "which rows are the middle"
// marking is done here.
function ReferenceDetail({ player, blockKey, metricKey, onClose }) {
  const closeRef = useRef(null);
  const block = player[blockKey];
  const mm = block.metrics[metricKey];
  useEffect(() => {
    closeRef.current?.focus();
    // Capture phase + stopImmediatePropagation: Escape closes this popup, not the drawer under it.
    const onKey = e => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const counted = (mm.reference || []).filter(r => r.value != null).sort((a, b) => b.value - a.value);
  const k = counted.length;
  const middleRows = k ? (k % 2 ? [counted[(k - 1) / 2]] : [counted[k / 2 - 1], counted[k / 2]]) : [];
  const middle = new Set(middleRows.map(r => r.playerId));
  const rows = [
    ...counted,
    ...(mm.value != null ? [{ playerId: 'self', name: player.name, position: player.positionGroup, value: mm.value, n: mm.n, meanTotal: mm.meanTotal, self: true }] : []),
  ].sort((a, b) => b.value - a.value)
    .concat((mm.reference || []).filter(r => r.value == null));
  const v = x => fmtValue(blockKey, metricKey, x);
  const metres = x => fmtValue('match', metricKey, x); // plain metres / count, no ×
  const isTraining = blockKey === 'training';
  const unit = blockKey === 'match' ? 'matches' : 'scenarios';
  const how = k % 2 ? 'the middle value' : `the average of the two middle values (${middleRows.map(r => v(r.value)).join(' and ')})`;
  const groupName = (referenceText(block) || '').split(':')[0];

  return (
    <div role="presentation" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={`${LONG_LABEL[metricKey]} — group values`} onClick={e => e.stopPropagation()}
        style={{ width: 'min(500px, 100%)', maxHeight: '100%', overflowY: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 12, padding: 14, boxSizing: 'border-box', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{LONG_LABEL[metricKey]} · {BLOCK_LABEL[blockKey]}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
              {groupName} · {blockKey === 'match' ? 'mean per 90, competitive matches of 60+ min' : 'mean of scenario total ÷ squad median'}
            </div>
          </div>
          <button ref={closeRef} onClick={onClose} aria-label="Close"
            style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', borderRadius: 8, padding: 5, cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
          <thead>
            <tr>
              <th style={{ ...cellTh, textAlign: 'left' }}>Player</th>
              {isTraining && <th style={cellTh} title="Mean training total per scenario over the counted scenarios — for reference">Avg scenario</th>}
              <th style={cellTh}>{isTraining ? '× squad median' : 'Per 90'}</th>
              <th style={cellTh}>n {unit}</th>
              <th style={cellTh} />
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.playerId} style={r.self ? { background: 'var(--bg-row-hover)' } : undefined}>
                <td style={{ ...cellTd, textAlign: 'left', fontWeight: r.self ? 700 : 400 }}>
                  {r.name} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({r.position})</span>
                </td>
                {isTraining && <td style={{ ...cellTd, color: 'var(--text-secondary)' }}>{metres(r.meanTotal)}</td>}
                <td style={{ ...cellTd, fontWeight: middle.has(r.playerId) ? 700 : 400 }}>{v(r.value)}</td>
                <td style={{ ...cellTd, color: 'var(--text-muted)' }}>{r.n ?? '—'}</td>
                <td style={{ ...cellTd, fontSize: 11, color: 'var(--text-muted)', textAlign: 'left' }}>
                  {r.self ? 'this player — not in the median' : r.value == null ? 'no valid value — not counted' : middle.has(r.playerId) ? 'median' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.5 }}>
          Group median <strong>{v(mm.groupMedian)}</strong> = {how} of {k} player{k === 1 ? '' : 's'}.
          {mm.value != null && mm.groupMedian != null && <> {player.name}: {v(mm.value)} → <strong>{fmtPct(mm.pctVsGroup)}</strong> vs the group.</>}
        </div>
        {isTraining && (
          <>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.45 }}>
              Training is compared on × squad median, not metres: per scenario, a player's total is divided by the squad
              median of that same scenario, so a long and a short scenario (number of days and sessions) count equally.
              Avg scenario (m) is shown for reference only.
            </div>
            <ScenarioBreakdown player={player} metricKey={metricKey} />
          </>
        )}
      </div>
    </div>
  );
}

export default function DevelopmentPlayerDrawer({ player, fullScreen, onClose }) {
  const closeRef = useRef(null);
  const [detail, setDetail] = useState(null); // { blockKey, metricKey }
  const openDetail = useCallback((blockKey, metricKey) => setDetail({ blockKey, metricKey }), []);
  const closeDetail = useCallback(() => setDetail(null), []);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const domain = useMemo(() => ratioDomain(BLOCKS.flatMap(b => METRICS.map(m => player[b].metrics[m.key].ratio))), [player]);
  const squadPos = player.positionGroup !== player.position ? ` (squad position ${player.position})` : '';

  return (
    <div role="presentation" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <aside role="dialog" aria-modal="true" aria-label={`Development — ${player.name}`} onClick={e => e.stopPropagation()}
        style={{ width: fullScreen ? '100vw' : 'min(720px, 100vw)', height: '100%', overflowY: 'auto', background: 'var(--bg-app)',
          borderLeft: fullScreen ? 'none' : '1px solid var(--border-strong)', padding: fullScreen ? 16 : 20, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{player.name}</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
              {player.positionGroup}{squadPos} · {player.minutesInWindow} competitive minutes in the period
            </div>
          </div>
          <button ref={closeRef} onClick={onClose} aria-label="Close"
            style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', borderRadius: 8, padding: 6, cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>

        <section style={{ marginTop: 16, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 6 }}>Key points</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.5 }}>
            {player.sentences.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            Suggestions with the numbers behind them. A value below the group is not necessarily a weakness — role and tactics matter.
          </div>
        </section>

        {BLOCKS.map(b => <BlockSection key={b} blockKey={b} block={player[b]} domain={domain} onDetail={openDetail} />)}
        {detail && (player[detail.blockKey].referenceUsed === 'target'
          ? <MatchTargetDetail player={player} metricKey={detail.metricKey} onClose={closeDetail} />
          : <ReferenceDetail player={player} blockKey={detail.blockKey} metricKey={detail.metricKey} onClose={closeDetail} />)}

        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
          Bars — training: player vs the median of the other players in his position group; match: his mean % of his position's
          match target. Dashed line = group median / target, shaded band = ±10% (inside it a metric is never a key point).
          Blue = above, orange = below — direction only, not good or bad.
          Training SD is shown but never picked; a metric with fewer valid points than the block minimum is shown but skipped.
          Click a bar to see the numbers behind it.
        </p>
      </aside>
    </div>
  );
}
